import React from 'react';
import { Link } from 'react-router-dom';

const FeaturesSection = () => {
  const features = [
    {
      image: '/images/all/ai-threat-detection-panel.png',
      title: 'AI Threat Detection',
      description: 'Our advanced AI system continuously monitors the blockchain for potential threats, suspicious activities, and emerging attack vectors.',
      benefits: [
        'Real-time threat monitoring',
        'Predictive risk analysis',
        'Automated response protocols',
        'Machine learning adaptation'
      ],
      stats: '99.7% threat detection accuracy',
      cta: 'Monitor Wallet'
    },
    {
      image: '/images/all/cold-storage-vault.png',
      title: 'Cold Storage Vault',
      description: 'Military-grade cold storage solution that keeps your private keys completely offline while maintaining instant access when needed.',
      benefits: [
        'Offline key storage',
        'Hardware security modules',
        'Multi-signature protocols',
        'Instant access capability'
      ],
      stats: '100% offline security guarantee',
      cta: 'Secure Wallet'
    },
    {
      image: '/images/all/transaction-monitor.png',
      title: 'Transaction Monitoring',
      description: 'Comprehensive transaction monitoring and analysis system that tracks all your crypto activities across multiple chains.',
      benefits: [
        'Multi-chain monitoring',
        'Real-time alerts',
        'Transaction history',
        'Smart contract analysis'
      ],
      stats: '24/7 monitoring coverage',
      cta: 'Track Wallet'
    }
  ];

  const securityFeatures = [
    {
      icon: '🔐',
      title: 'Zero-Knowledge Architecture',
      description: 'Your private keys never leave your device. We use zero-knowledge proofs to verify transactions without exposing sensitive data.',
      cta: 'Create Wallet'
    },
    {
      icon: '🛡️',
      title: 'Insurance Protection',
      description: 'All assets are covered by comprehensive insurance policies, providing additional peace of mind for your investments.',
      cta: 'Protect Wallet'
    },
    {
      icon: '🔑',
      title: 'Recovery Phrase Security',
      description: 'Advanced recovery phrase management with multiple backup locations and secure storage protocols.',
      cta: 'Backup Wallet'
    },
    {
      icon: '⚡',
      title: 'Instant Recovery',
      description: 'Quick and secure asset recovery process with multi-factor authentication and biometric verification.',
      cta: 'Recover Wallet'
    }
  ];

  return (
    <section className="py-12 lg:py-16 bg-deepspace-400">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 lg:mb-16">
        <div className="text-center" data-aos="fade-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Enterprise-Grade Security Features
          </h2>
          
          <p className="text-base sm:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Experience the most advanced Web3 security platform with cutting-edge features designed to protect your digital assets 
            from every possible threat vector.
          </p>
        </div>
      </div>

      {/* Main Features */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20 lg:space-y-32">
        {features.map((feature, index) => (
          <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
            {/* Image */}
            <div 
              className={`relative ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}
              data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}
              data-aos-delay="100"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyber-blue/20 via-cyber-gold/20 to-cyber-green/20 rounded-3xl blur-2xl scale-110"></div>
                <div className="relative bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-3xl p-8">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div 
              className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}
              data-aos={index % 2 === 0 ? "fade-left" : "fade-right"}
              data-aos-delay="200"
            >
              <div className="space-y-4">
                <h3 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-text-primary">
                  {feature.title}
                </h3>
                
                <p className="text-lg text-text-secondary leading-relaxed">
                  {feature.description}
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-3">
                {feature.benefits.map((benefit, benefitIndex) => (
                  <div key={benefitIndex} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-cyber-green rounded-full flex-shrink-0"></div>
                    <span className="text-text-primary">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="pt-4">
                <Link to="/auth" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-cyber-blue to-cyber-gold text-white font-semibold rounded-xl hover:scale-105 hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all duration-300">
                  {feature.cta}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Security Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-32">
        <div className="text-center mb-12 lg:mb-16" data-aos="fade-up">
          <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
            Additional Security Layers
          </h3>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Multiple layers of security working together to provide comprehensive protection for your digital assets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {securityFeatures.map((feature, index) => (
            <div 
              key={index}
              className="group bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-6 lg:p-8 text-center hover:border-cyber-blue/50 hover:bg-dark-card/70 transition-all duration-300 hover:scale-105 flex flex-col"
              data-aos="zoom-in"
              data-aos-delay={index * 100}
            >
              <div className="text-4xl lg:text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              
              <h4 className="text-lg lg:text-xl font-bold text-text-primary mb-3 group-hover:text-cyber-blue transition-colors duration-300">
                {feature.title}
              </h4>
              
              <p className="text-text-secondary text-sm lg:text-base leading-relaxed mb-6 flex-grow">
                {feature.description}
              </p>

              <Link to="/auth" className="inline-flex items-center justify-center px-4 py-2 bg-cyber-blue/10 border border-cyber-blue/30 text-cyber-blue font-semibold rounded-lg hover:bg-cyber-blue hover:text-white transition-all duration-300">
                {feature.cta}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 lg:mt-20">
        <div className="bg-gradient-to-r from-cyber-green/10 via-cyber-blue/10 to-cyber-gold/10 backdrop-blur-sm border border-cyber-green/30 rounded-3xl p-8 lg:p-12 text-center" data-aos="fade-up" data-aos-delay="200">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/images/all/insurance-protection-symbol.png" 
              alt="Insurance Protection"
              className="w-16 h-16 lg:w-20 lg:h-20 object-contain filter brightness-0 invert opacity-60"
            />
          </div>
          
          <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
            $100M Insurance Coverage
          </h3>
          <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
            Your assets are protected by comprehensive insurance coverage, providing additional security and peace of mind for all users.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth" className="px-8 py-4 bg-gradient-to-r from-cyber-green to-cyber-blue text-white font-semibold rounded-xl hover:scale-105 transition-transform duration-300 inline-block text-center">
              Connect Wallet
            </Link>
            <Link to="/auth" className="px-8 py-4 border border-cyber-green/50 text-cyber-green font-semibold rounded-xl backdrop-blur-sm hover:bg-cyber-green/10 transition-all duration-300 inline-block text-center">
              Create Wallet
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
